/**
 * @file TabEmails.jsx
 * @description TabEmails component for displaying and managing tab emails.
 * @component TabEmails
 * @exports TabEmails - Main component for displaying and managing tab emails.
 */

import React, { useState, useEffect } from "react";
import { Alert } from "@mui/material";
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Tooltip,
} from "@mui/material";
import ProgressOverlay from '../../../common/components/ProgressOverlay.jsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
	getcustomeremails,
	sendEmail,
} from "../../../services/customerinfoModule/historyServices";
import { getConversationCompliance } from '../../../services/customerinfoModule/historyServices';
import { getconversationcomplianceForR413 } from '../../../services/customerinfoModule/historyServices';
import CustomerComplianceModal from '../../customerModule/customers/CustomerComplianceModal.jsx';
import CustComplianceSummaryModal from '../../customerModule/customers/CustComplianceSummaryModal.jsx';
import { IconButton } from '@mui/material';
import { getcustomerprofilebyguid } from "../../../services/customerinfoModule/profileServices";
import { fetchCustomerEmailChart } from "../../../services/dashboardModule/dashboardServices";
import CustomerDashEmailChart from "../../dashboard/customersDash/charts/CustomerDashEmailChart.jsx";

const CustomerEmails = ({ customerguid, openSendEmail, setOpenSendEmail, isDash }) => {
	const [emails, setEmails] = useState([]);
	const [loading, setLoading] = useState(false);
	const [sendEmailValue, setSendEmailValue] = useState("");
	const [sendEmailSubject, setSendEmailSubject] = useState("");
	const [errorMsg, setErrorMsg] = useState("");
	const [successMessage, setSuccuessMessage] = useState("");
	const [complianceOpen, setComplianceOpen] = useState(false);
	const [complianceLoading, setComplianceLoading] = useState(false);
	const [complianceData, setComplianceData] = useState([]);
	const [complianceError, setComplianceError] = useState("");
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [summaryLoading, setSummaryLoading] = useState(false);
	const [summaryData, setSummaryData] = useState([]);
	const [summaryError, setSummaryError] = useState("");
	const [chartData, setChartData] = useState([]);

	useEffect(() => {
		const fetchEmails = async () => {
			setLoading(true);
			try {
				const data = await getcustomeremails({ customerguid });
				setEmails(data || []);
			} catch (error) {
				console.error("Error fetching emails:", error);
			} finally {
				setLoading(false);
			}
		};

		const fetchChart = async () => {
			try {
				const data = await fetchCustomerEmailChart(customerguid);
				setChartData(data || []);
			} catch (error) {
				console.error("Error fetching email chart:", error);
			}
		};

		if (customerguid) {
			fetchEmails();
			if (isDash) fetchChart();
		}
	}, [customerguid, isDash]);

	const formatDate = (isoDate) => {
		if (!isoDate) return "—";
		const date = new Date(isoDate);
		if (Number.isNaN(date.getTime())) return isoDate;
		const mm = String(date.getMonth() + 1).padStart(2, '0');
		const dd = String(date.getDate()).padStart(2, '0');
		const yyyy = date.getFullYear();
		return `${mm}/${dd}/${yyyy}`;
	};

	const openSummaryForConversation = async (convId) => {
		setSummaryError("");
		setSummaryData([]);
		setSummaryLoading(true);
		setSummaryOpen(true);
		try {
			const data = await getconversationcomplianceForR413({ conversationID: convId });
			setSummaryData(data || []);
		} catch (err) {
			console.error('Failed to fetch summary', err);
			setSummaryError('Failed to load summary details');
		} finally {
			setSummaryLoading(false);
		}
	};

	// group emails by ConversationID15
	const grouped = emails.reduce((acc, item) => {
		const key = item.ConversationID15 ?? 'Unknown';
		if (!acc[key]) acc[key] = [];
		acc[key].push(item);
		return acc;
	}, {});

	const openComplianceForConversation = async (convId) => {
		setComplianceError("");
		setComplianceData([]);
		setComplianceLoading(true);
		setComplianceOpen(true);
		try {
			const data = await getConversationCompliance({ conversationID: convId });
			setComplianceData(data || []);
		} catch (err) {
			console.error('Failed to fetch compliance', err);
			setComplianceError('Failed to load compliance details');
		} finally {
			setComplianceLoading(false);
		}
	};

	/* eslint-disable no-unused-vars */
	const _handleSendEmail = async () => {
	/* eslint-enable no-unused-vars */
		if (!sendEmailSubject.trim() && !sendEmailValue.trim()) {
			setErrorMsg("Subject and Message are required.");
			return;
		}
		if (!sendEmailSubject.trim()) {
			setErrorMsg("Subject is required.");
			return;
		}
		if (!sendEmailValue.trim()) {
			setErrorMsg("Message is required.");
			return;
		}
		try {
			setLoading(true);
			const customer = await getcustomerprofilebyguid({ customerguid });
			if (customer && customer[0].emailaddress) {
				const data = {
					emailaddress: customer[0].emailaddress,
					customerguid,
					subject: sendEmailSubject,
					message: sendEmailValue,
					userguid: localStorage.getItem("userguid"),
				};
				await sendEmail(data);
				setSuccuessMessage("Email sent successfully.");
				setTimeout(() => setSuccuessMessage(""), 3000);
			} else {
				setErrorMsg("Failed to send email. Customer email not found.");
			}
		} catch (error) {
			setErrorMsg("Failed to send email. Please try again.");
		} finally {
			setLoading(false);
			setErrorMsg("");
			setOpenSendEmail(false);
			setSendEmailValue("");
			setSendEmailSubject("");
		}
	};

	return (
		<Box sx={{ p: 2 }}>
			{isDash && (
				<CustomerDashEmailChart
					rows={chartData}
					title="Email Volume"
					label="Emails"
					isCurrency={false}
				/>
			)}
			{loading && <ProgressOverlay />}
			{errorMsg && (
				<Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
			)}
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
			)}
			{Object.keys(grouped).length === 0 && !loading ? (
				<Alert severity="info">No emails found</Alert>
			) : (
				Object.keys(grouped).map((convId) => (
					<Accordion key={convId} sx={{ mb: 1 }}>
						<AccordionSummary sx={{ display: 'flex', alignItems: 'center', flexDirection: "row-reverse" }} expandIcon={<ExpandMoreIcon />}>
							<Typography sx={{ ml: 1, fontSize: '0.875rem', flex: 1 }}>{`ConversationID - ${convId}`}</Typography>
							<Tooltip title="Complaince">
								<FactCheckOutlinedIcon sx={{ cursor: 'pointer', color: 'gray', mr: 1 }} onClick={(e) => { e.stopPropagation(); openComplianceForConversation(convId); }} aria-label={`compliance-${convId}`} />
							</Tooltip>
							<Tooltip title="R413 Summary">
								<IconButton sx={{ p: 0, color: 'gray' }} onClick={(e) => { e.stopPropagation(); openSummaryForConversation(convId); }} aria-label={`summary-${convId}`}>
									<DescriptionOutlinedIcon />
								</IconButton>
							</Tooltip>
						</AccordionSummary>
						<AccordionDetails>
							<TableContainer component={Paper}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={{ width: 300 }}><b>Customer</b></TableCell>
											<TableCell sx={{ width: 300 }}><b>Response</b></TableCell>
											<TableCell sx={{ width: 100 }}><b>Date</b></TableCell>
											<TableCell sx={{ width: 100 }}><b>Response Date</b></TableCell>
											<TableCell sx={{ width: 150 }}><b>Subject</b></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{grouped[convId].map((item, index) => (
											<TableRow key={index}>
												<TableCell sx={{ verticalAlign: "top" }} dangerouslySetInnerHTML={{ __html: item.userresponse }}></TableCell>
												<TableCell
													sx={{
														verticalAlign: "top",
														'& p:first-of-type': {
															marginTop: 0,
														},
													}}
													dangerouslySetInnerHTML={{ __html: item.cararesponse }}
												></TableCell>
												<TableCell sx={{ verticalAlign: "top" }}>{formatDate(item.userresponsetime)}</TableCell>
												<TableCell sx={{ verticalAlign: "top" }}>{formatDate(item.cararesponsetime)}</TableCell>
												<TableCell sx={{ verticalAlign: "top" }}>{item.userresponsesubject}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</AccordionDetails>
					</Accordion>
				))
			)}
				<CustomerComplianceModal
					open={complianceOpen}
					onClose={() => setComplianceOpen(false)}
					loading={complianceLoading}
					data={complianceData}
					error={complianceError}
				/>
						<CustComplianceSummaryModal
							open={summaryOpen}
							onClose={() => setSummaryOpen(false)}
							loading={summaryLoading}
							data={summaryData}
							error={summaryError}
						/>
		</Box>
	);
};

export default CustomerEmails;